import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  DollarSign,
  Download,
  FileText,
  Mail,
  Send,
  XCircle,
  Bell,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

import { pageTransition, formatDate, formatCurrency } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";
import {
  downloadPdf,
  getInvoice,
  issueInvoice,
  listPayments,
  listReminders,
  markInvoicePaid,
  recordPayment,
  sendInvoice,
  sendReminder,
  voidInvoice,
} from "@/api/invoices";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { SkeletonDetailPage } from "@/components/ui/Skeleton";
import {
  INVOICE_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  REMINDER_KIND_LABELS,
  type Invoice,
  type InvoiceStatus,
  type Payment,
  type PaymentMethod,
  type Reminder,
  type ReminderKind,
} from "@/types/invoice";

function statusBadgeVariant(s: InvoiceStatus) {
  switch (s) {
    case "PAID":
      return "success" as const;
    case "PARTIALLY_PAID":
      return "warning" as const;
    case "ISSUED":
    case "SENT":
    case "VIEWED":
      return "info" as const;
    case "OVERDUE":
      return "danger" as const;
    case "VOID":
      return "default" as const;
    default:
      return "default" as const;
  }
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { can } = usePermissions();
  const canManage = can("manage_invoices");

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);

  // Modals
  const [showPayment, setShowPayment] = useState(false);
  const [showVoid, setShowVoid] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showReminder, setShowReminder] = useState(false);

  // Form state
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState<PaymentMethod>("CHECK");
  const [payRef, setPayRef] = useState("");
  const [payDate, setPayDate] = useState("");
  const [payNotes, setPayNotes] = useState("");

  const [voidReason, setVoidReason] = useState("");
  const [sendEmail, setSendEmail] = useState("");
  const [reminderKind, setReminderKind] = useState<ReminderKind>("DUE_SOON");
  const [reminderEmail, setReminderEmail] = useState("");

  const fetchAll = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [inv, pays, rems] = await Promise.all([
        getInvoice(id),
        listPayments(id).catch(() => [] as Payment[]),
        listReminders(id).catch(() => [] as Reminder[]),
      ]);
      setInvoice(inv);
      setPayments(pays);
      setReminders(rems);
    } catch {
      addToast("error", "Failed to load invoice");
    } finally {
      setIsLoading(false);
    }
  }, [id, addToast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleIssue = async () => {
    if (!invoice) return;
    setIsBusy(true);
    try {
      await issueInvoice(invoice.id);
      addToast("success", "Invoice issued");
      await fetchAll();
    } catch {
      addToast("error", "Failed to issue invoice");
    } finally {
      setIsBusy(false);
    }
  };

  const handleSend = async () => {
    if (!invoice) return;
    setIsBusy(true);
    try {
      await sendInvoice(invoice.id, sendEmail.trim() || undefined);
      addToast("success", "Invoice sent");
      setShowSend(false);
      setSendEmail("");
      await fetchAll();
    } catch {
      addToast("error", "Failed to send invoice");
    } finally {
      setIsBusy(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!invoice) return;
    setIsBusy(true);
    try {
      await markInvoicePaid(invoice.id);
      addToast("success", "Invoice marked as paid");
      await fetchAll();
    } catch {
      addToast("error", "Failed to mark paid");
    } finally {
      setIsBusy(false);
    }
  };

  const handleVoid = async () => {
    if (!invoice || !voidReason.trim()) return;
    setIsBusy(true);
    try {
      await voidInvoice(invoice.id, voidReason.trim());
      addToast("success", "Invoice voided");
      setShowVoid(false);
      setVoidReason("");
      await fetchAll();
    } catch {
      addToast("error", "Failed to void invoice");
    } finally {
      setIsBusy(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!invoice) return;
    const amount = parseFloat(payAmount);
    if (!amount || amount <= 0) {
      addToast("error", "Enter a positive amount");
      return;
    }
    setIsBusy(true);
    try {
      await recordPayment(invoice.id, {
        amount,
        method: payMethod,
        reference: payRef.trim() || undefined,
        received_at: payDate || undefined,
        notes: payNotes.trim() || undefined,
      });
      addToast("success", "Payment recorded");
      setShowPayment(false);
      setPayAmount("");
      setPayRef("");
      setPayDate("");
      setPayNotes("");
      setPayMethod("CHECK");
      await fetchAll();
    } catch {
      addToast("error", "Failed to record payment");
    } finally {
      setIsBusy(false);
    }
  };

  const handleSendReminder = async () => {
    if (!invoice) return;
    setIsBusy(true);
    try {
      await sendReminder(
        invoice.id,
        reminderKind,
        reminderEmail.trim() || undefined,
      );
      addToast("success", "Reminder sent");
      setShowReminder(false);
      setReminderEmail("");
      await fetchAll();
    } catch (err: unknown) {
      const status =
        typeof err === "object" && err && "response" in err
          ? // @ts-expect-error - dynamic axios shape
            err.response?.status
          : undefined;
      if (status === 429) {
        addToast("error", "Reminder throttled — try again later");
      } else {
        addToast("error", "Failed to send reminder");
      }
    } finally {
      setIsBusy(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice) return;
    try {
      const blob = await downloadPdf(invoice.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice.invoice_number || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      addToast("error", "Failed to download PDF");
    }
  };

  if (isLoading) {
    // Full detail-shaped skeleton so the header, totals panel, and
    // payments history all land in place rather than jumping into
    // existence when the fetch resolves.
    return <SkeletonDetailPage />;
  }

  if (!invoice) {
    return (
      <div className="text-center py-16">
        <p className="text-text-muted">Invoice not found</p>
        <Button
          variant="ghost"
          onClick={() => navigate("/invoices")}
          className="mt-4"
        >
          Back to invoices
        </Button>
      </div>
    );
  }

  const status = invoice.status;
  const amountPaid = invoice.amount_paid ?? 0;
  const balanceDue = invoice.balance_due ?? Math.max(invoice.total - amountPaid, 0);
  const canIssue = canManage && status === "DRAFT";
  const canSend =
    canManage && (status === "DRAFT" || status === "ISSUED" || status === "SENT");
  const canRecordPayment =
    canManage &&
    status !== "VOID" &&
    status !== "PAID" &&
    status !== "DRAFT";
  const canMarkPaid =
    canManage &&
    (status === "SENT" ||
      status === "VIEWED" ||
      status === "OVERDUE" ||
      status === "PARTIALLY_PAID" ||
      status === "ISSUED");
  const canVoid =
    canManage && status !== "VOID" && status !== "PAID";
  const canRemind =
    canManage &&
    (status === "SENT" ||
      status === "VIEWED" ||
      status === "OVERDUE" ||
      status === "PARTIALLY_PAID");

  return (
    <motion.div {...pageTransition}>
      <button
        onClick={() => navigate("/invoices")}
        className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-text-primary mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to invoices
      </button>

      <PageHeader
        title={invoice.invoice_number || "Invoice"}
        subtitle={(() => {
          // Post-046 subtitle prefers the CLIENT (recipient) then the
          // employee (internal assignee). Either may be unset.
          const partyLabel =
            invoice.client_name ||
            invoice.client_company ||
            invoice.employee_name ||
            null;
          const period = `${formatDate(invoice.period_start, "MMM d")} – ${formatDate(
            invoice.period_end,
            "MMM d, yyyy",
          )}`;
          return partyLabel ? `${partyLabel} • ${period}` : period;
        })()}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusBadgeVariant(status)}>
              {INVOICE_STATUS_LABELS[status]}
            </Badge>
          </div>
        }
      />

      {/* Action row */}
      {canManage && (
        <div className="flex flex-wrap gap-2 mb-6">
          {canIssue && (
            <Button onClick={handleIssue} isLoading={isBusy}>
              <FileText className="h-4 w-4" />
              Issue
            </Button>
          )}
          {canSend && (
            <Button variant="secondary" onClick={() => setShowSend(true)}>
              <Send className="h-4 w-4" />
              Send
            </Button>
          )}
          {canRecordPayment && (
            <Button variant="secondary" onClick={() => setShowPayment(true)}>
              <DollarSign className="h-4 w-4" />
              Record payment
            </Button>
          )}
          {canMarkPaid && (
            <Button
              variant="secondary"
              onClick={handleMarkPaid}
              isLoading={isBusy}
            >
              <CheckCircle2 className="h-4 w-4" />
              Mark paid
            </Button>
          )}
          {canRemind && (
            <Button variant="ghost" onClick={() => setShowReminder(true)}>
              <Bell className="h-4 w-4" />
              Send reminder
            </Button>
          )}
          <Button variant="ghost" onClick={handleDownloadPdf}>
            <Download className="h-4 w-4" />
            Download PDF
          </Button>
          {canVoid && (
            <Button variant="ghost" onClick={() => setShowVoid(true)}>
              <XCircle className="h-4 w-4" />
              Void
            </Button>
          )}
        </div>
      )}

      {status === "VOID" && invoice.void_reason && (
        <Card className="mb-6 border-danger/30 bg-danger/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-danger mt-0.5" />
            <div>
              <p className="text-sm font-medium text-danger">Voided</p>
              <p className="text-sm text-text-muted mt-1">
                {invoice.void_reason}
              </p>
              {invoice.voided_at && (
                <p className="text-xs text-text-muted mt-1">
                  {formatDate(invoice.voided_at, "MMM d, yyyy h:mm a")}
                </p>
              )}
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column: line items + totals */}
        <div className="lg:col-span-2 space-y-6">
          <Card padding={false}>
            <div className="p-6 pb-3">
              <CardTitle>Line items</CardTitle>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-text-muted border-y border-border">
                  <tr>
                    <th className="px-6 py-2 font-medium">Description</th>
                    <th className="px-6 py-2 font-medium text-right">Hours</th>
                    <th className="px-6 py-2 font-medium text-right">Rate</th>
                    <th className="px-6 py-2 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice.line_items_json || []).map((li, idx) => (
                    <tr key={idx} className="border-b border-border last:border-0">
                      <td className="px-6 py-3 text-text-primary">
                        {li.description || "—"}
                      </td>
                      <td className="px-6 py-3 text-right text-text-muted">
                        {li.hours ?? "—"}
                      </td>
                      <td className="px-6 py-3 text-right text-text-muted">
                        {formatCurrency(li.rate, invoice.currency)}
                      </td>
                      <td className="px-6 py-3 text-right font-medium text-text-primary">
                        {formatCurrency(li.amount, invoice.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-6 pt-4 border-t border-border">
              <div className="ml-auto max-w-xs space-y-1.5 text-sm">
                <div className="flex justify-between text-text-muted">
                  <span>Subtotal</span>
                  <span>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
                </div>
                {invoice.tax_amount > 0 && (
                  <div className="flex justify-between text-text-muted">
                    <span>Tax ({invoice.tax_rate}%)</span>
                    <span>{formatCurrency(invoice.tax_amount, invoice.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-text-primary border-t border-border pt-1.5">
                  <span>Total</span>
                  <span>{formatCurrency(invoice.total, invoice.currency)}</span>
                </div>
                {invoice.total_in_base !== undefined &&
                  invoice.fx_rate_to_base !== undefined &&
                  Math.abs(invoice.fx_rate_to_base - 1) > 1e-9 && (
                    <div className="flex justify-between text-xs text-text-muted">
                      <span>≈ in base currency</span>
                      <span>{formatCurrency(invoice.total_in_base)}</span>
                    </div>
                  )}
                <div className="flex justify-between text-success">
                  <span>Paid</span>
                  <span>{formatCurrency(amountPaid, invoice.currency)}</span>
                </div>
                <div className="flex justify-between font-semibold text-text-primary">
                  <span>Balance due</span>
                  <span>{formatCurrency(balanceDue, invoice.currency)}</span>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payments</CardTitle>
            </CardHeader>
            {payments.length === 0 ? (
              <p className="text-sm text-text-muted">No payments recorded.</p>
            ) : (
              <div className="space-y-2">
                {payments.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-lg border border-border px-4 py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-text-primary">
                        {formatCurrency(p.amount, invoice.currency)}{" "}
                        <span className="text-text-muted font-normal">
                          • {PAYMENT_METHOD_LABELS[p.method]}
                        </span>
                      </p>
                      <p className="text-xs text-text-muted">
                        {formatDate(p.received_at)}
                        {p.reference ? ` • ${p.reference}` : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <dl className="space-y-2 text-sm">
              {invoice.due_date && (
                <div>
                  <dt className="text-xs text-text-muted">Due date</dt>
                  <dd className="text-text-primary">
                    {formatDate(invoice.due_date)}
                  </dd>
                </div>
              )}
              {/* Bill-to block: the external client (recipient). Falls
                  back to the legacy employee field for pre-046 rows. */}
              {(invoice.client_name ||
                invoice.client_company ||
                invoice.recipient_email ||
                invoice.client_email ||
                invoice.sent_to_email) && (
                <div>
                  <dt className="text-xs text-text-muted">Bill to</dt>
                  <dd className="text-text-primary">
                    {invoice.client_name || invoice.client_company || (
                      <span className="text-text-muted italic">
                        Unnamed recipient
                      </span>
                    )}
                    {invoice.client_company &&
                      invoice.client_name &&
                      invoice.client_company !== invoice.client_name && (
                        <span className="text-text-muted">
                          {" "}
                          ({invoice.client_company})
                        </span>
                      )}
                    {(invoice.recipient_email ||
                      invoice.client_email ||
                      invoice.sent_to_email) && (
                      <div className="text-xs text-text-muted">
                        {invoice.recipient_email ||
                          invoice.client_email ||
                          invoice.sent_to_email}
                      </div>
                    )}
                  </dd>
                </div>
              )}
              {invoice.employee_name && (
                <div>
                  <dt className="text-xs text-text-muted">Assigned to</dt>
                  <dd className="text-text-primary">{invoice.employee_name}</dd>
                </div>
              )}
              {invoice.issued_at && (
                <div>
                  <dt className="text-xs text-text-muted">Issued</dt>
                  <dd className="text-text-primary">
                    {formatDate(invoice.issued_at)}
                  </dd>
                </div>
              )}
              {invoice.sent_at && (
                <div>
                  <dt className="text-xs text-text-muted">Sent</dt>
                  <dd className="text-text-primary">
                    {formatDate(invoice.sent_at)}
                    {invoice.sent_to_email
                      ? ` • ${invoice.sent_to_email}`
                      : ""}
                  </dd>
                </div>
              )}
              {invoice.paid_at && (
                <div>
                  <dt className="text-xs text-text-muted">Paid</dt>
                  <dd className="text-text-primary">
                    {formatDate(invoice.paid_at)}
                  </dd>
                </div>
              )}
              {invoice.terms && (
                <div>
                  <dt className="text-xs text-text-muted">Terms</dt>
                  <dd className="text-text-primary">{invoice.terms}</dd>
                </div>
              )}
              {invoice.notes && (
                <div>
                  <dt className="text-xs text-text-muted">Notes</dt>
                  <dd className="text-text-primary whitespace-pre-wrap">
                    {invoice.notes}
                  </dd>
                </div>
              )}
            </dl>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Reminders</CardTitle>
            </CardHeader>
            {reminders.length === 0 ? (
              <p className="text-sm text-text-muted">No reminders sent yet.</p>
            ) : (
              <ul className="space-y-2">
                {reminders.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-start gap-2 text-sm"
                  >
                    <Mail className="h-4 w-4 text-accent mt-0.5" />
                    <div>
                      <p className="text-text-primary">
                        {REMINDER_KIND_LABELS[r.kind]}
                      </p>
                      <p className="text-xs text-text-muted">
                        {formatDate(r.created_at)} • {r.sent_to_email}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      {/* Record payment modal */}
      <Modal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        title="Record payment"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Amount"
            type="number"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            placeholder="0.00"
          />
          <Select
            label="Method"
            value={payMethod}
            onChange={(e) => setPayMethod(e.target.value as PaymentMethod)}
            options={Object.entries(PAYMENT_METHOD_LABELS).map(([v, l]) => ({
              value: v,
              label: l,
            }))}
          />
          <Input
            label="Reference (optional)"
            value={payRef}
            onChange={(e) => setPayRef(e.target.value)}
            placeholder="Check #, txn id, etc."
          />
          <Input
            label="Received at (optional)"
            type="date"
            value={payDate}
            onChange={(e) => setPayDate(e.target.value)}
          />
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Notes (optional)
            </label>
            <textarea
              value={payNotes}
              onChange={(e) => setPayNotes(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowPayment(false)}>
              Cancel
            </Button>
            <Button onClick={handleRecordPayment} isLoading={isBusy}>
              Record
            </Button>
          </div>
        </div>
      </Modal>

      {/* Void modal */}
      <Modal
        isOpen={showVoid}
        onClose={() => setShowVoid(false)}
        title="Void invoice"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            Voiding an invoice cannot be undone. Please describe why.
          </p>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Reason
            </label>
            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
              placeholder="e.g., issued in error"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowVoid(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleVoid} isLoading={isBusy}>
              Void invoice
            </Button>
          </div>
        </div>
      </Modal>

      {/* Send modal */}
      <Modal
        isOpen={showSend}
        onClose={() => setShowSend(false)}
        title="Send invoice"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="To email (optional — defaults to employee)"
            type="email"
            value={sendEmail}
            onChange={(e) => setSendEmail(e.target.value)}
            placeholder="billing@example.com"
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowSend(false)}>
              Cancel
            </Button>
            <Button onClick={handleSend} isLoading={isBusy}>
              Send
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reminder modal */}
      <Modal
        isOpen={showReminder}
        onClose={() => setShowReminder(false)}
        title="Send reminder"
        size="md"
      >
        <div className="space-y-4">
          <Select
            label="Kind"
            value={reminderKind}
            onChange={(e) => setReminderKind(e.target.value as ReminderKind)}
            options={Object.entries(REMINDER_KIND_LABELS).map(([v, l]) => ({
              value: v,
              label: l,
            }))}
          />
          <Input
            label="To email (optional)"
            type="email"
            value={reminderEmail}
            onChange={(e) => setReminderEmail(e.target.value)}
            placeholder="billing@example.com"
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowReminder(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendReminder} isLoading={isBusy}>
              Send reminder
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
